<script setup>
import { computed } from 'vue'
import { Link } from '@inertiajs/vue3'
import { Container } from '@codinglabsau/ui'
import { Button } from '@codinglabsau/gooey'
import VoyagesTable from '@/Components/Voyages/Table.vue'
import { ship_image } from '@/helpers'
import HeadMetaTags from '@/Components/HeadMetaTags.vue'
import SubSection from '@/Components/SubSection.vue'
import PageHeader from '@/Components/PageHeader.vue'
import Versions from '@/Components/Versions.vue'
import Versionable from '@/Components/Versionable.vue'
import { useVersionable } from '@/Composables/useVersionable'

const props = defineProps({
  ship: Object,
  voyages: Object,
  can: Object,
  leftVersionId: {
    type: Number,
    default: null,
  },
  leftVersion: {
    type: Object,
    default: null,
  },
  rightVersionId: {
    type: Number,
    default: null,
  },
  rightVersion: {
    type: Object,
    default: null,
  },
})

const { selectVersion } = useVersionable()

const versions = computed(() => [props.leftVersion ?? props.ship, props.rightVersion])
</script>

<template>
  <HeadMetaTags
    :title="ship.name"
    :description="`A history of the ship ${ship.name} as it transported convicts to Australia.`"
    :canonical="route('ships.show', ship)"
    :url="route('ships.show', ship)"
    :no-index="leftVersionId > 0 || rightVersionId > 0"
  />

  <Container>
    <PageHeader>
      <Versionable :versions="versions" attribute="name" />

      <template #actions>
        <Versions
          :versionable="ship"
          :version-ids="[leftVersionId, rightVersionId]"
          :current-url="route('ships.show', ship)"
          @update:version="selectVersion"
        />

        <Button v-if="can.edit" variant="outline" as-child>
          <Link :href="route('ships.edit', ship)">Edit</Link>
        </Button>
      </template>
    </PageHeader>

    <article class="bg-white p-4 sm:px-8">
      <section
        class="w-md my-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:items-start"
      >
        <Versionable v-slot="{ changed }" :versions="versions" attribute="image">
          <div v-if="changed" class="relative w-1/5">
            <img
              :src="ship_image(leftVersion.image)"
              class="bg-cover ring-2 ring-green-700"
              :alt="leftVersion.name"
            />

            <div class="absolute right-2 top-2 ring-2 ring-red-300">
              <img
                :src="ship_image(rightVersion.image)"
                class="relative h-16"
                :alt="rightVersion.name"
              />
              <span
                class="absolute bottom-0 left-0 bg-foreground text-center text-xs text-background opacity-70"
              >
                (previous)
              </span>
            </div>
          </div>

          <div v-else class="w-1/5">
            <img :src="ship_image(ship.image)" class="mr-4 rounded-lg bg-cover" :alt="ship.name" />
          </div>
        </Versionable>

        <div class="w-4/5 grow whitespace-pre-line">
          <p class="prose">
            <Versionable :versions="versions" attribute="description" />
          </p>
        </div>
      </section>

      <SubSection title="Voyages">
        <template #actions>
          <Button v-if="can.add_voyage" variant="outline" as-child>
            <Link :href="route('voyages.create') + '?ship_id=' + ship.id"> Add Voyage</Link>
          </Button>
        </template>

        <VoyagesTable :voyages="voyages" hide-paginator :searchable="false" :sortable="false" />
      </SubSection>
    </article>
  </Container>
</template>
